import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

const COLOR_THEMES = {
  blue: {
    primary: "#2f7de5",
    primaryDark: "#1f5ba7",
    primaryLight: "#e9f2ff",
  },
  green: {
    primary: "#10b981",
    primaryDark: "#059669",
    primaryLight: "#d1fae5",
  },
  purple: {
    primary: "#8b5cf6",
    primaryDark: "#7c3aed",
    primaryLight: "#ede9fe",
  },
  orange: {
    primary: "#f59e0b",
    primaryDark: "#d97706",
    primaryLight: "#fef3c7",
  },
  red: {
    primary: "#ef4444",
    primaryDark: "#dc2626",
    primaryLight: "#fee2e2",
  },
  teal: {
    primary: "#14b8a6",
    primaryDark: "#0d9488",
    primaryLight: "#ccfbf1",
  },
};

export const ThemeProvider = ({ children }) => {
  const [currentPage, setCurrentPage] = useState(null);
  const [analysisTheme, setAnalysisTheme] = useState(() => {
    const savedTheme = localStorage.getItem("analysisTheme");
    return savedTheme || "dark";
  });

  const [colorTheme, setColorTheme] = useState(() => {
    const savedColorTheme = localStorage.getItem("colorTheme");
    return savedColorTheme || "blue";
  });

  const getEffectiveTheme = () => {
    if (currentPage === "home") {
      return "light";
    }
    if (currentPage === "analysis") {
      return analysisTheme;
    }
    return "light";
  };

  const theme = getEffectiveTheme();

  useEffect(() => {
    localStorage.setItem("analysisTheme", analysisTheme);
    localStorage.setItem("colorTheme", colorTheme);
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.setAttribute("data-color-theme", colorTheme);

    const colorThemeData = COLOR_THEMES[colorTheme];
    if (colorThemeData) {
      document.documentElement.style.setProperty(
        "--theme-primary",
        colorThemeData.primary
      );
      document.documentElement.style.setProperty(
        "--theme-primary-dark",
        colorThemeData.primaryDark
      );
      document.documentElement.style.setProperty(
        "--theme-primary-light",
        colorThemeData.primaryLight
      );
    }
  }, [theme, colorTheme, analysisTheme]);

  const toggleTheme = () => {
    if (currentPage === "analysis") {
      setAnalysisTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
    }
  };

  const setPage = (page) => {
    setCurrentPage(page);
  };

  const setColorThemeValue = (color) => {
    setColorTheme(color);
  };

  const value = {
    theme,
    toggleTheme,
    isLight: theme === "light",
    isDark: theme === "dark",
    colorTheme,
    setColorTheme: setColorThemeValue,
    colorThemes: COLOR_THEMES,
    setPage,
    canToggleTheme: currentPage === "analysis",
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
